import { Component, Input, forwardRef } from '@angular/core';
import type { ControlValueAccessor} from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Themed native select that implements ControlValueAccessor for use in reactive and template forms.
 */
@Component({
  selector: 'es-select',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectComponent),
    multi: true,
  }],
  template: `
    <select
      [disabled]="isDisabled"
      [value]="value"
      class="w-full px-3 py-2 text-sm rounded-lg bg-[var(--bg-secondary)] text-[var(--text)] border border-[var(--border)] focus:outline-none focus:border-echo-blue focus:ring-1 focus:ring-echo-blue/30 transition-colors font-exo disabled:opacity-50 cursor-pointer"
      (change)="onSelect($event)"
    >
      @if (placeholder) {
        <option value="" disabled>{{ placeholder }}</option>
      }
      @for (opt of options; track opt.value) {
        <option [value]="opt.value">{{ opt.label }}</option>
      }
    </select>
  `,
})
export class SelectComponent implements ControlValueAccessor {
  @Input() options: SelectOption[] = [];
  @Input() placeholder?: string;

  value = '';
  isDisabled = false;
  onChange: (value: string) => void = () => {
    // noop — replaced by registerOnChange/registerOnTouched
  };
  onTouched: () => void = () => {
    // noop — replaced by registerOnChange/registerOnTouched
  };

  writeValue(value: string): void { this.value = value ?? ''; }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.isDisabled = disabled; }

  onSelect(event: Event): void {
    const val = (event.target as HTMLSelectElement).value;
    this.value = val;
    this.onChange(val);
    this.onTouched();
  }
}
